// src/components/rodeoAi/ChatHeader.jsx
import React from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Tooltip,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import logo from '../../images/rodeoLogo.png';


const ChatHeader = ({
    mode = 'full',
    credits,
    onRefreshCredits,
    onResetConversation,
    conversationId,
    contextInfo
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const getCreditsColor = () => {
        const percentage = (credits.balance / credits.monthlyAllowance) * 100;
        if (percentage <= 10) return 'error';
        if (percentage <= 20) return 'warning';
        return 'success';
    };

    const getCreditsPercentage = () => {
        return Math.min(100, (credits.balance / credits.monthlyAllowance) * 100);
    };

    // Compact mobile layout
    if (isMobile) {
        return (
            <Box sx={{ mb: 1.5 }}>
                {/* Single row with logo, title, and credits */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                    }}
                >
                    {/* Logo + Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                flexShrink: 0,
                                boxShadow: '0 2px 8px rgba(0,127,255,0.3)'
                            }}
                        >
                            <img
                                src={logo}
                                alt="Rodeo Logo"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            RodeoAI
                        </Typography>
                    </Box>

                    {/* Credits Display - Compact */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1.5,
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(19, 47, 76, 0.6)'
                                    : 'rgba(255, 255, 255, 0.9)',
                            border: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : '1px solid rgba(0, 0, 0, 0.1)',
                            flexShrink: 0
                        }}
                    >
                        {credits.loading ? (
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                ...
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {credits.balance}/{credits.monthlyAllowance}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={onRefreshCredits}
                                    disabled={credits.loading}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        padding: 0,
                                        '&:hover': {
                                            transform: 'rotate(90deg)'
                                        }
                                    }}
                                >
                                    <RefreshIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Progress Bar */}
                {!credits.loading && (
                    <LinearProgress
                        variant="determinate"
                        value={getCreditsPercentage()}
                        color={getCreditsColor()}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.1)'
                                    : 'rgba(0,0,0,0.1)'
                        }}
                    />
                )}

                {/* Context Info */}
                {contextInfo?.hasContext && (
                    <Chip
                        icon={<InfoIcon sx={{ fontSize: 14 }} />}
                        label={`${contextInfo.queryCount} ${contextInfo.queryCount === 1 ? 'query' : 'queries'}`}
                        size="small"
                        sx={{
                            mt: 1,
                            height: 24,
                            fontSize: '0.7rem',
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(0, 127, 255, 0.15)'
                                    : 'rgba(0, 127, 255, 0.1)',
                            color: '#007FFF',
                            border: '1px solid rgba(0, 127, 255, 0.3)',
                            '& .MuiChip-icon': {
                                color: '#007FFF',
                                fontSize: 14
                            }
                        }}
                    />
                )}
            </Box>
        );
    }

    // Tablet and Desktop layout
    return (
        <Box sx={{ mb: 2 }}>
            {/* Main Header Row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    flexWrap: isTablet ? 'wrap' : 'nowrap',
                    gap: 2
                }}
            >
                {/* Logo + Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: mode === 'full' ? 40 : 32,
                            height: mode === 'full' ? 40 : 32,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0,127,255,0.3)'
                        }}
                    >
                        <img
                            src={logo}
                            alt="Rodeo Logo"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant={mode === 'full' ? 'h5' : 'h6'}
                                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                >
                                    RodeoAI Agent
                                </Typography>
                                <Chip
                                    label="BETA"
                                    size="small"
                                    sx={{
                                        height: { xs: 18, sm: 20 },
                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? 'rgba(0, 127, 255, 0.2)'
                                                : 'rgba(0, 127, 255, 0.15)',
                                        color: '#007FFF',
                                        border: '1px solid rgba(0, 127, 255, 0.3)',
                                        '& .MuiChip-label': {
                                            px: { xs: 0.75, sm: 1 }
                                        }
                                    }}
                                />
                            </Box>
                            {mode === 'full' && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                                >
                                    Powered by Market Rodeo
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Credits Display */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                                ? 'rgba(19, 47, 76, 0.6)'
                                : 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: (theme) =>
                            theme.palette.mode === 'dark'
                                ? '1px solid rgba(255, 255, 255, 0.1)'
                                : '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: (theme) =>
                            theme.palette.mode === 'dark'
                                ? '0 4px 12px rgba(0,0,0,0.3)'
                                : '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                >
                    {credits.loading ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Loading...
                        </Typography>
                    ) : (
                        <>
                            {/* Credits Count */}
                            <Box sx={{ textAlign: 'right', minWidth: mode === 'full' ? 60 : 50 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                >
                                    {credits.balance}/{credits.monthlyAllowance}
                                </Typography>
                                {mode === 'full' && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '0.7rem',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        {credits.tier}
                                    </Typography>
                                )}
                            </Box>

                            {/* Progress Bar (Full mode only) */}
                            {mode === 'full' && (
                                <Box sx={{ width: 80 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={getCreditsPercentage()}
                                        color={getCreditsColor()}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(0,0,0,0.1)'
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Refresh credits">
                                    <IconButton
                                        size="small"
                                        onClick={onRefreshCredits}
                                        disabled={credits.loading}
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'rotate(90deg)',
                                                backgroundColor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255,255,255,0.1)'
                                                        : 'rgba(0,0,0,0.05)'
                                            }
                                        }}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* Context Info (if exists) */}
            {contextInfo?.hasContext && mode === 'full' && (
                <Chip
                    icon={<InfoIcon />}
                    label={`Using context from ${contextInfo.queryCount} previous ${contextInfo.queryCount === 1 ? 'query' : 'queries'
                        }`}
                    size="small"
                    sx={{
                        mt: 1,
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                                ? 'rgba(0, 127, 255, 0.15)'
                                : 'rgba(0, 127, 255, 0.1)',
                        color: '#007FFF',
                        border: '1px solid rgba(0, 127, 255, 0.3)',
                        '& .MuiChip-icon': {
                            color: '#007FFF'
                        }
                    }}
                />
            )}
        </Box>
    );
};

export default ChatHeader;